import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const BatchVerification = ({ onBatchProcess }) => {
  const [batchItems, setBatchItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [results, setResults] = useState([]);
  const fileInputRef = useRef(null);

  const mockBatchCredentials = [
    {
      id: 'batch_001',
      name: 'Stanford_CS_Degree.json',
      type: 'EducationalCredential',
      size: '2.4 KB',
      status: 'pending'
    },
    {
      id: 'batch_002',
      name: 'AWS_Certification.json',
      type: 'ProfessionalCertification',
      size: '1.8 KB',
      status: 'pending'
    },
    {
      id: 'batch_003',
      name: 'Medical_License.json',
      type: 'ProfessionalLicense',
      size: '3.1 KB',
      status: 'pending'
    }
  ];

  const handleFileUpload = (event) => {
    const files = Array.from(event?.target?.files);
    const newItems = files?.map((file, index) => ({
      id: `batch_${Date.now()}_${index}`,
      name: file?.name,
      type: 'VerifiableCredential',
      size: `${(file?.size / 1024)?.toFixed(1)} KB`,
      status: 'pending',
      file: file
    }));
    
    setBatchItems(prev => [...prev, ...newItems]);
  };

  const handleAddMockData = () => {
    setBatchItems(prev => [...prev, ...mockBatchCredentials]);
  };

  const handleRemoveItem = (id) => {
    setBatchItems(prev => prev?.filter(item => item?.id !== id));
  };

  const handleClearAll = () => {
    setBatchItems([]);
    setResults([]);
    setProcessingProgress(0);
  };

  const processBatch = async () => {
    if (batchItems?.length === 0) return;

    setIsProcessing(true);
    setProcessingProgress(0);
    const batchResults = [];

    for (let i = 0; i < batchItems?.length; i++) {
      const item = batchItems?.[i];
      
      // Update item status to processing
      setBatchItems(prev => prev?.map(prevItem => 
        prevItem?.id === item?.id 
          ? { ...prevItem, status: 'processing' }
          : prevItem
      ));

      try {
        // Simulate processing time
        await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1000));

        // Simulate verification result
        const success = Math.random() > 0.2; // 80% success rate
        const result = {
          id: item?.id,
          name: item?.name,
          status: success ? 'verified' : 'failed',
          confidence: success ? Math.floor(85 + Math.random() * 15) : Math.floor(20 + Math.random() * 40),
          processingTime: Math.floor(1500 + Math.random() * 1000),
          timestamp: new Date()?.toISOString(),
          details: success ? {
            issuer: 'Verified Issuer',
            signatureValid: true,
            notRevoked: true,
            notExpired: true
          } : {
            error: 'Signature verification failed',
            reason: 'Invalid cryptographic signature'
          }
        };

        batchResults?.push(result);

        // Update item status
        setBatchItems(prev => prev?.map(prevItem => 
          prevItem?.id === item?.id 
            ? { ...prevItem, status: result?.status }
            : prevItem
        ));

        // Update progress
        setProcessingProgress(((i + 1) / batchItems?.length) * 100);

      } catch (error) {
        const result = {
          id: item?.id,
          name: item?.name,
          status: 'failed',
          confidence: 0,
          processingTime: 0,
          timestamp: new Date()?.toISOString(),
          error: error?.message
        };

        batchResults?.push(result);

        setBatchItems(prev => prev?.map(prevItem => 
          prevItem?.id === item?.id 
            ? { ...prevItem, status: 'failed' }
            : prevItem
        ));
      }
    }

    setResults(batchResults);
    setIsProcessing(false);
    onBatchProcess?.(batchResults);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return 'CheckCircle';
      case 'failed': return 'XCircle';
      case 'processing': return 'Loader2';
      default: return 'Clock';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-success';
      case 'failed': return 'text-destructive';
      case 'processing': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const exportResults = () => {
    const exportData = {
      batchId: `batch_${Date.now()}`,
      timestamp: new Date()?.toISOString(),
      totalItems: batchItems?.length,
      results: results,
      summary: {
        verified: results?.filter(r => r?.status === 'verified')?.length,
        failed: results?.filter(r => r?.status === 'failed')?.length,
        averageConfidence: results?.reduce((sum, r) => sum + r?.confidence, 0) / results?.length
      }
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-verification-results-${Date.now()}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Icon name="Layers" size={20} className="text-primary mr-2" />
            <h3 className="font-semibold text-foreground">Batch Verification</h3>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">
              {batchItems?.length} item{batchItems?.length !== 1 ? 's' : ''}
            </span>
            {isProcessing && (
              <div className="flex items-center text-sm text-primary">
                <Icon name="Loader2" size={16} className="animate-spin mr-1" />
                Processing...
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Upload Section */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".json,.jwt"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef?.current?.click()}
              iconName="Upload"
              iconPosition="left"
              disabled={isProcessing}
              className="w-full sm:w-auto"
            >
              Upload Credentials
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddMockData}
              iconName="Plus"
              iconPosition="left"
              disabled={isProcessing}
            >
              Add Sample Data
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              iconName="Trash2"
              iconPosition="left"
              disabled={isProcessing}
            >
              Clear All
            </Button>
          </div>
        </div>

        {/* Processing Progress */}
        {isProcessing && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Processing Batch</span>
              <span className="text-sm text-muted-foreground">
                {Math.round(processingProgress)}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${processingProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>
      {/* Batch Items List */}
      <div className="max-h-64 overflow-y-auto">
        {batchItems?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="FileText" size={48} className="text-muted-foreground/50 mx-auto mb-4" />
            <h4 className="font-medium text-foreground mb-2">No Credentials Added</h4>
            <p className="text-sm text-muted-foreground">
              Upload credential files or add sample data to get started
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {batchItems?.map((item) => (
              <div key={item?.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    item?.status === 'verified' ? 'bg-success/10' :
                    item?.status === 'failed' ? 'bg-destructive/10' :
                    item?.status === 'processing'? 'bg-primary/10' : 'bg-muted'
                  }`}>
                    <Icon 
                      name={getStatusIcon(item?.status)} 
                      size={16} 
                      className={`${getStatusColor(item?.status)} ${
                        item?.status === 'processing' ? 'animate-spin' : ''
                      }`}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground truncate">{item?.name}</h4>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <span>{item?.type}</span>
                      <span>•</span>
                      <span>{item?.size}</span>
                      {results?.find(r => r?.id === item?.id) && (
                        <>
                          <span>•</span>
                          <span className={getStatusColor(item?.status)}>
                            {results?.find(r => r?.id === item?.id)?.confidence}% confidence
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveItem(item?.id)}
                  disabled={isProcessing}
                  className="ml-2"
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="p-4 border-t border-border">
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="default"
            onClick={processBatch}
            disabled={batchItems?.length === 0 || isProcessing}
            loading={isProcessing}
            iconName="Play"
            iconPosition="left"
            className="flex-1"
          >
            {isProcessing ? 'Processing...' : `Verify ${batchItems?.length} Credential${batchItems?.length !== 1 ? 's' : ''}`}
          </Button>
          
          {results?.length > 0 && (
            <Button
              variant="outline"
              onClick={exportResults}
              iconName="Download"
              iconPosition="left"
              className="flex-1"
            >
              Export Results
            </Button>
          )}
        </div>

        {/* Results Summary */}
        {results?.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center p-3 bg-success/10 rounded-lg">
              <div className="text-2xl font-bold text-success">
                {results?.filter(r => r?.status === 'verified')?.length}
              </div>
              <div className="text-sm text-muted-foreground">Verified</div>
            </div>
            
            <div className="text-center p-3 bg-destructive/10 rounded-lg">
              <div className="text-2xl font-bold text-destructive">
                {results?.filter(r => r?.status === 'failed')?.length}
              </div>
              <div className="text-sm text-muted-foreground">Failed</div>
            </div>
            
            <div className="text-center p-3 bg-primary/10 rounded-lg">
              <div className="text-2xl font-bold text-primary">
                {Math.round(results?.reduce((sum, r) => sum + r?.confidence, 0) / results?.length)}%
              </div>
              <div className="text-sm text-muted-foreground">Avg. Confidence</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchVerification;